'use client';

import { PropsWithChildren, createContext, useContext, useRef, useState } from 'react';
import { toast } from 'sonner';

import { TOAST_DURATIONS } from 'lib/config/sonner';

import { useSetArmLocked } from '../../page/manual-control/spot-arm/components/useLockArm';

type RobotArmSpeedContextType = {
  armSpeed: number;
  increaseArmSpeed: VoidFunction;
  decreaseArmSpeed: VoidFunction;
  changeArmSpeed: (speed: number) => void;
};

const RobotArmSpeedContext = createContext<RobotArmSpeedContextType | null>(null);

export function RobotArmSpeedProvider({ children }: PropsWithChildren) {
  const [armSpeed, setArmSpeed] = useState<number>(0.5);
  const toastId = useRef<string | number | null>(null);
  const { setArmUnlock } = useSetArmLocked();

  function changeArmSpeed(speed: number) {
    setArmUnlock();

    const newSpeed = Math.round(speed * 100) / 100;
    setArmSpeed(newSpeed);

    const message = `Changed Arm Speed to ${newSpeed}`;

    if (toastId.current) {
      toast.dismiss(toastId.current);
    }
    toastId.current = toast.info(message, { duration: TOAST_DURATIONS.SHORT, onDismiss: () => toastId.current = null });
  }

  function increaseArmSpeed() {
    if (armSpeed < 1) {
      changeArmSpeed(armSpeed + 0.05);
    }
  }

  function decreaseArmSpeed() {
    if (armSpeed > 0) {
      changeArmSpeed(armSpeed - 0.05);
    }
  }

  return (
    <RobotArmSpeedContext.Provider
      value={{ armSpeed, increaseArmSpeed, decreaseArmSpeed, changeArmSpeed }}
    >
      {children}
    </RobotArmSpeedContext.Provider>
  );
}

/**
 * The robot arm have to manually be unspeed to prevent accidental movement
 */
export function useRobotArmSpeed(): RobotArmSpeedContextType {
  const context = useContext(RobotArmSpeedContext);
  if (!context) {
    throw new Error('useRobotArmSpeed must be used within a RobotArmSpeedProvider');
  }
  return context;
}

